import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TaskAPI, PlaylistsAPI } from "../../api/api";
import "./Tasks.css";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approveNeeded, setApprovNeeded] = useState(false);
  const [showAapproved, setShowAapproved] = useState(false);

  const [pageIndex, setPageIndex] = useState(0);
  const [weekPages, setWeekPages] = useState([]);
  const [changedTasks, setChangedTasks] = useState(new Set());

  const navigate = useNavigate();

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await TaskAPI.getAll();
      setTasks(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Failed to load tasks:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    if (!tasks.length) return;

    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const todayUtc = new Date();
    todayUtc.setUTCHours(0, 0, 0, 0);
    const todayIndex = todayUtc.getUTCDay();

    const pages = [];

    for (let i = 0; i < 7; i++) {
      const dayIndex = (todayIndex + i) % 7;
      const label = i === 0 ? "Today" : days[dayIndex];
      const pageDate = new Date(todayUtc);
      pageDate.setUTCDate(todayUtc.getUTCDate() + i);

      const dayTasks = tasks
        .filter((t) => t.isActive)
        .filter((t) => {
          switch (t.repeatability) {
            case "Daily":
              return true;
            case "Weekly":
              return t.weeklyDay && t.weeklyDay === days[dayIndex];
            case "Once":
              if (!t.startTime) return false;
              const startDate = new Date(t.startTime);
              const startDateUTC = new Date(Date.UTC(
                startDate.getUTCFullYear(),
                startDate.getUTCMonth(),
                startDate.getUTCDate()
              ));
              return startDateUTC.getTime() === pageDate.getTime();
            default:
              return false;
          }
        })
        .map((t) => {
          let time = null;

          if (t.repeatability === "Daily") {
            time = t.dailyTime ? utcTimeToLocalHM(t.dailyTime) : "-";
          } else if (t.repeatability === "Weekly") {
            time = t.weeklyTime ? utcTimeToLocalHM(t.weeklyTime) : "-";
          } else if (t.repeatability === "Once") {
            time = t.startTime ? formatTimeLocalHM(t.startTime) : "-";
          }

          return {
            id: t.id,
            time,
            name: t.name,
            desc: t.description,
          };
        })
        .sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99"));

      pages.push({
        label,
        key: days[dayIndex],
        date: pageDate.toISOString().split("T")[0],
        tasks: dayTasks,
      });
    }

    setWeekPages(pages);
  }, [tasks]);

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await TaskAPI.delete(id);
      loadTasks();
    } catch (err) {
      console.error("Failed to delete task:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkboxHandler = async (id) => {
    const playlists = await checkPlaylists();

    if (playlists == null) return;

    setTasks((prevTasks) =>
      prevTasks.map((task) => {
        if (task.id === id) {
          const isCurrentlyActive = task.isActive;

          if (!isCurrentlyActive) {
            const playlistExists = playlists.some(
              (p) => p.name === task.activePlaylist
            );

            if (!playlistExists) {
              alert(
                "Cannot activate this task. Playlist not found. You should edit or delete it."
              );
              return task;
            }
          }

          const updatedTask = { ...task, isActive: !task.isActive };

          setApprovNeeded(true);
          setChangedTasks((prev) => {
            const copy = new Set(prev);
            copy.add(task.id);
            return copy;
          });

          return updatedTask;
        }
        return task;
      })
    );
  };

  const checkPlaylists = async () => {
    try {
      const res = await PlaylistsAPI.getPlaylists();
      return (res.data);
    } catch (err) {
      return null;
    }
  }

  const handleEdit = (task) => {
    navigate(`/tasks/edit/${task.id}`);
  };

  const handleApprove = async () => {
    const payload = tasks
      .filter((t) => changedTasks.has(t.id))
      .map((t) => ({
        id: t.id,
        isActive: t.isActive,
      }));

    try {
      await TaskAPI.aprrove(payload);
      setApprovNeeded(false)
      showApprovedText()
    } catch (err) {
      console.error("Failed to approve tasks:", err);
    }

    setChangedTasks(new Set());
  };

  const showApprovedText = () => {
    setShowAapproved(true);

    setTimeout(() => {
      setShowAapproved(false);
    }, 3000);
  }

  const formatStart = (task) => {
    switch (task.repeatability) {
      case "Once":
        if (!task.startTime) return "-";
        const d = new Date(task.startTime + "Z");
        const dateDay = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${dateDay}-${month}-${year} - ${hours}:${minutes}`;
      case "Daily":
        if (!task.dailyTime) return "-";
        const [dH, dM] = task.dailyTime.split(":").map(Number);
        const now = new Date();
        now.setUTCHours(dH, dM, 0, 0); // treat as UTC
        return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      case "Weekly":
        const dayMap = {
          Sunday: "Sunday",
          Monday: "Monday",
          Tuesday: "Tuesday",
          Wednesday: "Wednesday",
          Thursday: "Thursday",
          Friday: "Friday",
          Saturday: "Saturday"
        };
        const dayName = dayMap[task.weeklyDay] || "-";
        if (!task.weeklyTime) return `${dayName} - -`;
        const [wH, wM] = task.weeklyTime.split(":").map(Number);
        const wDate = new Date();
        wDate.setUTCHours(wH, wM, 0, 0); // treat as UTC
        return `${dayName} - ${String(wDate.getHours()).padStart(2, "0")}:${String(wDate.getMinutes()).padStart(2, "0")}`;
      default:
        return "-";
    }
  };

  const utcTimeToLocalHM = (utcTimeString) => {
    if (!utcTimeString) return "-";
    const [h, m] = utcTimeString.split(":").map(Number);

    const d = new Date();
    d.setUTCHours(h, m, 0, 0);

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
  };

  const formatTimeLocalHM = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString + "Z");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const tasksMain = () => {
    return (
      <div className="tasks-main">
        <div className="task-main-title"><h1>TASKS</h1></div>
        {loading
          ? <div className="spinner"></div>
          :
          <>
            <div className="task-main-btn"><button onClick={() => navigate("/tasks/create")} className="add-btn" >ADD TASK</button></div>

            <div className="table-main">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="t-id">Id</th>
                      <th className="t-name">Name</th>
                      <th className="t-desc">Description</th>
                      <th className="t-playlist">Playlist</th>
                      <th className="t-rep">Repeat</th>
                      <th className="t-start">Start At</th>
                      <th className="t-active">Active</th>
                      <th className="t-btn"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task) => (
                      <tr key={task.id}>
                        <td className="t-id">{task.id}</td>
                        <td className="t-name">{task.name}</td>
                        <td className="t-desc">{task.description}</td>
                        <td className="t-playlist">{task.activePlaylist}</td>
                        <td className="t-rep">{task.repeatability}</td>
                        <td className="t-start">{formatStart(task)}</td>
                        <td className="t-active">
                          <div onClick={() => checkboxHandler(task.id)}>
                            {task.isActive
                              ? <i className="fa-solid fa-check"></i>
                              : <i className="fa-regular fa-square"></i>}
                          </div>
                        </td>
                        <td className="t-btn">
                          <button
                            onClick={() => handleEdit(task)}
                            className="edit-btn"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(task.id)}
                            className="del-btn"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        }
      </div>
    );
  };

  const guidePreviw = () => {
    const colorLeftArrow = pageIndex === 0 ? "#666666ff" : "#F5F5F3";
    const colorRightArrow = pageIndex === weekPages.length - 1 ? "#666666ff" : "#F5F5F3";

    return (
      <div className="guid-preview">
        <div className="guid-preview-main">
          {approveNeeded && <div className="not-approved">Must be approved to take effect</div>}
          {showAapproved && <div className="approved-msg">Successfully approved</div>}
          <div className="guid-preview-title"><h1>GUIDE PREVIEW</h1></div>

          <div className="guide-main-container">
            <div className="m-expanded-guide">
              <div className="m-expanded-guide-content">
                <div className="m-expanded-guide-title">
                  <div className="m-text">UPCOMING NEXT WEEK</div>
                  <div className="m-date-arrows">
                    <div className="m-left-arrow">
                      <button
                        style={{ color: colorLeftArrow }}
                        onClick={() => { setPageIndex((i) => Math.max(0, i - 1)) }}
                      >
                        <i className="fa-solid fa-chevron-left"></i>
                      </button>
                    </div>
                    <div className="m-day">{weekPages[pageIndex]?.label}</div>
                    <div className="m-right-arrow">
                      <button
                        style={{ color: colorRightArrow }}
                        onClick={() => {
                          if (pageIndex === weekPages.length - 1) {
                            console.log("stretch effect here");
                          } else {
                            setPageIndex((i) => Math.min(weekPages.length - 1, i + 1));
                          }
                        }}
                      >
                        <i className="fa-solid fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="m-expanded-guide-list-main">
                  <div
                    className="m-expanded-guide-list"
                    style={{
                      display: "flex",
                      width: `${weekPages.length * 100}%`,
                      transform: `translateX(calc(${-pageIndex * (100 / weekPages.length)}%)`,
                      transition: "transform 0.3s ease",
                    }}
                  >
                    {weekPages.map((day, idx) => (
                      <div key={`${day.key}-${idx}`} className="page" style={{ flex: `0 0 calc(100% / ${weekPages.length})` }}>
                        <ul>
                          {day.tasks?.map((task, idx, arr) => (
                            <li key={`${pageIndex}-${task.id}-${idx}`}>
                              <div className="start-end-time">{task.time}</div>
                              <div className="task-content">
                                <div className="task-name">{task.name}</div>
                                <div className="task-description">{task.desc}</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="guid-preview-btn"><button className="add-btn" onClick={handleApprove}>APPROVE</button></div>
        </div>
      </div>
    );
  };

  return (
    <div className="main">
      {tasksMain()}
      {guidePreviw()}
    </div>
  );
}
